import * as React from 'react';
import { AlertTriangle } from 'lucide-react';
import { Button } from '@org/ui';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@org/ui';
import { useTranslation } from 'react-i18next';

type EventDeleteConfirmDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  eventTitle: string;
  isSeriesEvent: boolean;
  onConfirmDelete: (deleteSeries: boolean) => void;
  isDeleting?: boolean;
};

export function EventDeleteConfirmDialog({
  open,
  onOpenChange,
  eventTitle,
  isSeriesEvent,
  onConfirmDelete,
  isDeleting = false,
}: EventDeleteConfirmDialogProps) {
  const { t } = useTranslation('admin');

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-full max-w-md">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-destructive/10">
              <AlertTriangle className="h-5 w-5 text-destructive" />
            </div>
            <div className="flex-1">
              <DialogTitle>{t('events.deleteDialog.title')}</DialogTitle>
              <DialogDescription>{eventTitle}</DialogDescription>
            </div>
          </div>
        </DialogHeader>

        {isSeriesEvent ? (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              {t('events.deleteDialog.seriesQuestion')}
            </p>
            <div className="flex flex-col gap-2">
              <Button
                variant="outline"
                onClick={() => onConfirmDelete(false)}
                disabled={isDeleting}
                className="h-auto w-full justify-start py-3"
              >
                <span className="font-semibold">{t('events.deleteDialog.deleteOccurrence')}</span>
              </Button>
              <Button
                variant="destructive"
                onClick={() => onConfirmDelete(true)}
                disabled={isDeleting}
                isLoading={isDeleting}
                loadingText={t('common.status.deleting')}
                className="h-auto w-full flex-col items-start gap-0.5 py-3"
              >
                <span className="font-semibold">{t('events.deleteDialog.deleteSeries')}</span>
                <span className="text-xs font-normal opacity-90">{t('events.deleteDialog.futureOccurrences')}</span>
              </Button>
              <Button
                variant="ghost"
                onClick={() => onOpenChange(false)}
                disabled={isDeleting}
                className="w-full"
              >
                {t('common.actions.cancel')}
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              {t('events.deleteDialog.confirmSingle')}
            </p>
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={isDeleting}
              >
                {t('common.actions.cancel')}
              </Button>
              <Button
                variant="destructive"
                onClick={() => onConfirmDelete(false)}
                disabled={isDeleting}
                isLoading={isDeleting}
                loadingText={t('common.status.deleting')}
              >
                {t('events.deleteDialog.title')}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
